import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setActiveDialog } from "src/store/adminHtml/actions";
import {
  activeDialogPropsSelector,
  activeDialogSelector,
} from "src/store/adminHtml/selector";

type DialogEntity = "inventory" | "sale" | "transaction";

type DialogResult =
  | { status: "updated" }
  | { status: "cancelled"; reason: "closed" | "interrupted" | "unmounted" };

const DialogByEntity = {
  inventory: "edit_inventory",
  sale: "edit_sale",
  transaction: "edit_transaction",
} as const;

function newRequestId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useOpenDialog() {
  const dispatch = useDispatch();
  const activeDialog = useSelector(activeDialogSelector);
  const activeDialogProps = useSelector(activeDialogPropsSelector) as
    | { requestId?: string; updated?: boolean }
    | undefined;
  const pendingRef = useRef<
    | {
        requestId: string;
        activeDialog: (typeof DialogByEntity)[DialogEntity];
        resolve: (result: DialogResult) => void;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    const isSameDialog = activeDialog === pending.activeDialog;
    const isSameRequest = activeDialogProps?.requestId === pending.requestId;
    if (isSameDialog && isSameRequest) return;

    pendingRef.current = undefined;
    if (
      activeDialog === "" &&
      activeDialogProps?.requestId === pending.requestId &&
      activeDialogProps.updated
    ) {
      pending.resolve({ status: "updated" });
      return;
    }
    pending.resolve({
      status: "cancelled",
      reason: activeDialog === "" ? "closed" : "interrupted",
    });
  }, [activeDialog, activeDialogProps]);

  useEffect(() => {
    return () => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = undefined;
      pending.resolve({ status: "cancelled", reason: "unmounted" });
    };
  }, []);

  return useCallback(
    (entity: DialogEntity, id: string) => {
      return new Promise<DialogResult>((resolve) => {
        const previous = pendingRef.current;
        if (previous) {
          pendingRef.current = undefined;
          previous.resolve({ status: "cancelled", reason: "interrupted" });
        }

        const requestId = newRequestId();
        const dialog = DialogByEntity[entity];
        pendingRef.current = { requestId, activeDialog: dialog, resolve };
        if (entity === "transaction") {
          dispatch(setActiveDialog("edit_transaction", { id, requestId }));
        } else if (entity === "inventory") {
          dispatch(setActiveDialog("edit_inventory", { id, requestId }));
        } else {
          dispatch(setActiveDialog("edit_sale", { id, requestId }));
        }
      });
    },
    [dispatch]
  );
}
